import { useState, useEffect, useRef } from "react";
import {
  DollarSign,
  Search,
  Filter,
  Receipt,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Eye,
  X,
  AlertCircle,
  Loader2,
  ChevronDown,
  Plus,
  Upload,
  Trash2,
  Edit,
  Check,
} from "lucide-react";
import BaseApi from "../../services/base-api";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";
import Price from "../../components/Currency/Price";
import LoadingComponent from "../../components/LoadingComponent";
import { useAuth } from "../../context/AuthContext";
import Pagination from "../../components/Pagination";
import { useDebounce } from "../../hooks/useDebounce";

// Types
interface Expense {
  id: string;
  expenseNumber: string;
  approver: {
    id: string;
    firstName: string;
    lastName: string;
  };
  category: string;
  amount: number;
  currency: string;
  description: string;
  date: string;
  status: "pending" | "approved" | "rejected" | "reimbursed";
  attachments: string[];
  notes?: string;
  approvedBy?: {
    id: string;
    name: string;
  };
  approvedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

interface ExpenseFormData {
  category: string;
  amount: string;
  description: string;
  date: string;
  attachments: File[];
  notes?: string;
}

interface ExpenseStatsOverview {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  totalAmount: number;
}

const ExpensesManagementPage = () => {
  const { isAuthenticated, user } = useAuth();
  const isAdmin = isAuthenticated && user?.role === "admin";
  const [apiTotalItems, setApiTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState<"date" | "amount">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [filtered, setFiltered] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const latestRequestRef = useRef(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim());
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [detailsModal, setDetailsModal] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [updating, setUpdating] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [adminExpenseTab, setAdminExpenseTab] = useState<
    "approval_requests" | "all_expenses"
  >("approval_requests");
  const [currentUserId, setCurrentUserId] = useState("user-123");
  const [currentUserName, setCurrentUserName] = useState("");
  const [employees, setEmployees] = useState<any[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<any[]>([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState<ExpenseFormData>({
    category: "",
    amount: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
    attachments: [],
    notes: "",
  });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  // Kept separately so the label survives when search results change
  const [selectedEmployeeName, setSelectedEmployeeName] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const debouncedEmployeeSearch = useDebounce(employeeSearch.trim());
  const latestEmployeesRequestRef = useRef(0);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [expenseStatsOverview, setExpenseStatsOverview] =
    useState<ExpenseStatsOverview>({
      total: 0,
      pending: 0,
      approved: 0,
      rejected: 0,
      totalAmount: 0,
    });

  // Rejection modal states
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [expenseToReject, setExpenseToReject] = useState<string | null>(null);

  const { t, i18n } = useTranslation();
  const { success, err } = useToast();

  const expenseStatuses = [
    {
      value: "pending",
      label: "Pending",
      color: "bg-yellow-100 text-yellow-800",
      icon: Clock,
    },
    {
      value: "approved",
      label: "Approved",
      color: "bg-green-100 text-green-800",
      icon: CheckCircle,
    },
    {
      value: "rejected",
      label: "Rejected",
      color: "bg-red-100 text-red-800",
      icon: XCircle,
    },
    {
      value: "reimbursed",
      label: "Reimbursed",
      color: "bg-blue-100 text-blue-800",
      icon: DollarSign,
    },
  ];

  const dateFilters = [
    { value: "all", label: "All Time" },
    { value: "today", label: "Today" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "quarter", label: "This Quarter" },
    { value: "year", label: "This Year" },
  ];

  const adminTabs = [
    { value: "all_expenses" as const, label: "All Expenses" },
    { value: "approval_requests" as const, label: "Approval Requests" },
  ];

  useEffect(() => {
    setCurrentUserId(user?.id || "");
    setCurrentUserName(user?.firstName || "");

    fetchCategories();
    fetchExpenseStatsOverview();
  }, [isAdmin, user?.id, user?.firstName]);

  // Employee picker in the create modal searches users on the server
  useEffect(() => {
    if (!isAdmin || !createModal) return;
    fetchEmployees(debouncedEmployeeSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmployeeSearch, isAdmin, createModal]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, categoryFilter, dateFilter]);

  useEffect(() => {
    fetchExpenses();
  }, [
    isAdmin,
    adminExpenseTab,
    currentPage,
    itemsPerPage,
    debouncedSearch,
    categoryFilter,
    statusFilter,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, categoryFilter, statusFilter, adminExpenseTab]);

  const fetchExpenses = async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      if (categoryFilter !== "all") {
        params.append("category", categoryFilter);
      }
      if (isAdmin && adminExpenseTab === "approval_requests") {
        params.append("status", "pending");
      } else if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }

      const endpoint = isAdmin
        ? `/expenses?${params.toString()}`
        : `/expenses/my-expenses?${params.toString()}`;

      const res = await BaseApi.get(endpoint);
      if (requestId !== latestRequestRef.current) return;
      if (res.success) {
        setExpenses(res.data || []);
        setFiltered(res.data || []);
        setApiTotalItems(res.pagination?.totalItems || res.data.length);
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error("Error fetching expenses:", error);
      err(t("Failed to load expenses"));
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  };

  const fetchExpenseStatsOverview = async () => {
    try {
      const res = await BaseApi.get("/expenses/stats/overview");
      console.log("Expense stats overview response:", res);
      const stats = res?.data ?? res;
      setExpenseStatsOverview({
        total: Number(stats?.total) || 0,
        pending: Number(stats?.pending) || 0,
        approved: Number(stats?.approved) || 0,
        rejected: Number(stats?.rejected) || 0,
        totalAmount: Number(stats?.totalAmount) || 0,
      });
    } catch (error) {
      console.error("Error fetching expense stats overview:", error);
    }
  };

  const isCustomerUser = (u: any) =>
    String(typeof u?.role === "string" ? u.role : u?.role?.name || "")
      .trim()
      .toLowerCase() === "customer";

  const getEmployeeLabel = (emp: any) => {
    const name = `${emp?.firstName ?? ""} ${emp?.lastName ?? ""}`.trim();
    const phone = emp?.phone ? `${emp.phoneCode ?? ""}-${emp.phone}` : "";
    return name || phone || emp?.email || "";
  };

  const fetchEmployees = async (query = "") => {
    const requestId = ++latestEmployeesRequestRef.current;
    try {
      setEmployeesLoading(true);
      const params = new URLSearchParams({ limit: "100" });
      if (query.trim()) {
        params.append("search", query.trim());
      }
      const res = await BaseApi.get(
        `/users?${params.toString()}`,
        {},
        { skipLoader: true },
      );
      if (requestId !== latestEmployeesRequestRef.current) return;
      if (res.success) {
        // Expenses belong to staff, never to customers
        setEmployees((res.data || []).filter((u: any) => !isCustomerUser(u)));
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      if (requestId === latestEmployeesRequestRef.current) {
        setEmployeesLoading(false);
      }
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await BaseApi.get("/expenses/categories/list");
      if (res.success) {
        setExpenseCategories(res.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Check if user can edit/delete expense (only for pending/rejected and own expenses)
  const canEditExpense = (expense: Expense) => {
    if (isAdmin) {
      // Admin cannot edit expenses created by employees
      return false;
    }
    // Employee can only edit their own pending or rejected expenses
    return (
      expense.approver.id === currentUserId &&
      (expense.status === "pending" || expense.status === "rejected")
    );
  };

  const canDeleteExpense = (expense: Expense) => {
    if (isAdmin) {
      // Admin cannot delete expenses created by employees
      return false;
    }
    // Employee can only delete their own pending or rejected expenses
    return (
      expense.approver.id === currentUserId &&
      (expense.status === "pending" || expense.status === "rejected")
    );
  };

  const handleCreateExpense = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.category ||
      !formData.amount ||
      !formData.description ||
      !formData.date
    ) {
      err(t("Please fill in all required fields"));
      return;
    }

    if (isAdmin && !selectedEmployeeId && !isEditMode) {
      err(t("Please select an employee"));
      return;
    }

    try {
      setSubmitting(true);

      // Create FormData for file upload
      const data = new FormData();
      data.append("category", formData.category);
      data.append("amount", formData.amount);
      data.append("description", formData.description);
      data.append("date", formData.date);
      if (formData.notes) {
        data.append("notes", formData.notes);
      }

      // Add employee ID
      if (isEditMode) {
        // In edit mode, keep the original employee
        // Don't send employeeId as it shouldn't change
      } else {
        // In create mode
        if (isAdmin) {
          // Admin selects employee
          data.append("userId", selectedEmployeeId);
        } else {
          // Employee creates for themselves
          data.append("userId", currentUserId);
        }
      }

      // Append files
      if (!isEditMode) {
        formData.attachments.forEach((file) => {
          data.append("attachments", file);
        });
      } else {
        // In edit mode, only append new files
        formData.attachments.forEach((file) => {
          if (file instanceof File) {
            data.append("attachments", file);
          }
        });
      }

      let res;
      if (isEditMode && selectedExpense) {
        res = await BaseApi.patch(`/expenses/${selectedExpense.id}`, data);
      } else {
        res = await BaseApi.post("/expenses", data);
      }

      if (res.success) {
        success(
          t(
            isEditMode
              ? "Expense updated successfully"
              : "Expense created successfully",
          ),
        );
        setCreateModal(false);
        setEditModal(false);
        resetForm();
        fetchExpenses();
        fetchExpenseStatsOverview();
      }
    } catch (error) {
      console.error("Error saving expense:", error);
      err(
        t(isEditMode ? "Failed to update expense" : "Failed to create expense"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveExpense = async (expenseId: string) => {
    if (!isAdmin) {
      err(t("Only admins can approve expenses"));
      return;
    }

    try {
      setUpdating(true);
      const res = await BaseApi.patch(`/expenses/${expenseId}/approve`, {
        status: "approved",
      });

      if (res.success) {
        setExpenses(
          expenses.map((e) =>
            e.id === expenseId
              ? {
                  ...e,
                  status: "approved",
                  approvedAt: new Date().toISOString(),
                }
              : e,
          ),
        );
        if (selectedExpense?.id === expenseId) {
          setSelectedExpense({
            ...selectedExpense,
            status: "approved",
            approvedAt: new Date().toISOString(),
          });
        }
        fetchExpenseStatsOverview();
        success(t("Expense approved successfully"));
      }
    } catch (error) {
      console.error("Error approving expense:", error);
      err(t("Failed to approve expense"));
    } finally {
      setUpdating(false);
    }
  };

  const openRejectModal = (expenseId: string) => {
    setExpenseToReject(expenseId);
    setRejectionReason("");
    setRejectModal(true);
  };

  const handleRejectExpense = async () => {
    if (!expenseToReject) return;

    if (!rejectionReason.trim()) {
      err(t("Please provide a rejection reason"));
      return;
    }

    if (!isAdmin) {
      err(t("Only admins can reject expenses"));
      return;
    }

    try {
      setUpdating(true);
      const res = await BaseApi.patch(`/expenses/${expenseToReject}/approve`, {
        reason: rejectionReason.trim(),
        status: "rejected",
      });

      if (res.success) {
        setExpenses(
          expenses.map((e) =>
            e.id === expenseToReject
              ? {
                  ...e,
                  status: "rejected",
                  rejectionReason: rejectionReason.trim(),
                }
              : e,
          ),
        );
        if (selectedExpense?.id === expenseToReject) {
          setSelectedExpense({
            ...selectedExpense,
            status: "rejected",
            rejectionReason: rejectionReason.trim(),
          });
        }
        success(t("Expense rejected"));
        setRejectModal(false);
        setExpenseToReject(null);
        setRejectionReason("");
        fetchExpenseStatsOverview();
      }
    } catch (error) {
      console.error("Error rejecting expense:", error);
      err(t("Failed to reject expense"));
    } finally {
      setUpdating(false);
    }
  };

  const handleEditExpense = (expense: Expense) => {
    if (!canEditExpense(expense)) {
      err(t("You cannot edit this expense"));
      return;
    }

    setSelectedExpense(expense);
    setFormData({
      category: expense.category,
      amount: expense.amount.toString(),
      description: expense.description,
      date: expense.date,
      attachments: [], // Existing attachments will be shown separately
      notes: expense.notes || "",
    });
    setIsEditMode(true);
    setEditModal(true);
  };

  const handleDeleteExpense = async (expenseId: string) => {
    const expense = expenses.find((e) => e.id === expenseId);
    if (!expense || !canDeleteExpense(expense)) {
      err(t("You cannot delete this expense"));
      return;
    }

    if (!window.confirm(t("Are you sure you want to delete this expense?"))) {
      return;
    }

    try {
      setUpdating(true);
      const res = await BaseApi.delete(`/expenses/${expenseId}`);

      if (res.success) {
        setExpenses(expenses.filter((e) => e.id !== expenseId));
        success(t("Expense deleted successfully"));
        setDetailsModal(false);
        setSelectedExpense(null);
        fetchExpenseStatsOverview();
      }
    } catch (error) {
      console.error("Error deleting expense:", error);
      err(t("Failed to delete expense"));
    } finally {
      setUpdating(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(
      (file) => file.size <= 5 * 1024 * 1024, // 5MB limit
    );

    if (validFiles.length !== files.length) {
      err(t("Some files exceed 5MB limit"));
    }

    setFormData({
      ...formData,
      attachments: [...formData.attachments, ...validFiles],
    });
  };

  const removeAttachment = (index: number) => {
    setFormData({
      ...formData,
      attachments: formData.attachments.filter((_, i) => i !== index),
    });
  };

  const resetForm = () => {
    setFormData({
      category: "",
      amount: "",
      description: "",
      date: new Date().toISOString().split("T")[0],
      attachments: [],
      notes: "",
    });
    // Admin creates for themselves by default
    setSelectedEmployeeId(isAdmin ? user?.id || "" : "");
    setSelectedEmployeeName(isAdmin ? getEmployeeLabel(user) : "");
    setEmployeeSearch("");
    setShowEmployeeDropdown(false);
    setIsEditMode(false);
    setSelectedExpense(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    return (
      expenseStatuses.find((s) => s.value === status)?.color ||
      "bg-gray-100 text-gray-800"
    );
  };

  const getCategoryName = (cat: any) =>
    cat.name?.[i18n.language] || cat.name?.en || cat.id;

  const getCategoryLabel = (category: string) => {
    const cat = expenseCategories.find((c) => c.id === category);
    return cat ? `${cat.icon} ${getCategoryName(cat)}` : category;
  };

  // Pagination calculations
  const finalTotalItems = apiTotalItems;
  const finalTotalPages = Math.ceil(finalTotalItems / itemsPerPage);
  const currentPageData = expenses;

  if (initialLoading) {
    return <LoadingComponent title={t("Expenses")} />;
  }

  return (
    <div className="min-h-screen bg-[#f2e6db] py-4 sm:py-8 px-3 sm:px-4 lg:px-6">
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      <div className="mx-auto">
        {/* Header */}
        <div
          className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6"
          style={{ animation: "slideDown 0.5s" }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <div className="bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
                <Receipt className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="flex-1 sm:flex-none">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  {t("Expenses")}
                </h1>
                <p className="text-sm sm:text-base text-gray-600">
                  {isAdmin
                    ? adminExpenseTab === "all_expenses"
                      ? t("Manage all employee expenses")
                      : t("Review employee expense requests")
                    : t("Submit and track your expenses")}
                </p>
              </div>
            </div>

            <button
              onClick={() => {
                resetForm();
                setCreateModal(true);
              }}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
            >
              <Plus className="w-5 h-5" />
              {isAdmin ? t("Add Expense") : t("New Expense")}
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-4 mt-4 sm:mt-6">
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-gray-600">{t("Total")}</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">
                {expenseStatsOverview.total}
              </p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-yellow-700">
                {t("Pending")}
              </p>
              <p className="text-xl sm:text-2xl font-bold text-yellow-900">
                {expenseStatsOverview.pending}
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-green-700">
                {t("Approved")}
              </p>
              <p className="text-xl sm:text-2xl font-bold text-green-900">
                {expenseStatsOverview.approved}
              </p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-red-700">{t("Rejected")}</p>
              <p className="text-xl sm:text-2xl font-bold text-red-900">
                {expenseStatsOverview.rejected}
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 sm:p-4 col-span-2 lg:col-span-1">
              <p className="text-xs sm:text-sm text-blue-700">
                {t("Total Expense")}
              </p>
              <p className="text-xl sm:text-2xl font-bold text-blue-900">
                <Price amount={expenseStatsOverview.totalAmount} />
              </p>
            </div>
          </div>

          {isAdmin && (
            <div className="mt-4 sm:mt-6 flex flex-wrap gap-2">
              {adminTabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setAdminExpenseTab(tab.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    adminExpenseTab === tab.value
                      ? "bg-black text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {t(tab.label)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          {/* Mobile Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4"
          >
            <span className="flex items-center gap-2 font-medium">
              <Filter className="w-5 h-5" />
              {t("Filters")}
            </span>
            <ChevronDown
              className={`w-5 h-5 transition-transform ${showFilters ? "rotate-180" : ""}`}
            />
          </button>

          {/* Filter Inputs */}
          <div
            className={`grid grid-cols-1 gap-3 sm:gap-4 ${showFilters ? "block" : "hidden"} lg:grid ${
              isAdmin && adminExpenseTab === "approval_requests"
                ? "lg:grid-cols-3"
                : "lg:grid-cols-4"
            }`}
          >
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder={t("Search expenses...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            {(!isAdmin || adminExpenseTab === "all_expenses") && (
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent appearance-none bg-white"
                >
                  <option value="all">{t("All Status")}</option>
                  {expenseStatuses
                    .filter((status) => status.value !== "reimbursed")
                    .map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent appearance-none bg-white"
              >
                <option value="all">{t("All Categories")}</option>
                {expenseCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {/* {category.icon} */}
                    {getCategoryName(category)}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Filter */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5 pointer-events-none" />
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent appearance-none bg-white"
              >
                {dateFilters.map((filter) => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Expenses Table */}
        <div
          className={
            "bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden" +
            (loading ? " opacity-50 pointer-events-none transition-opacity" : "")
          }
        >
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Expense")} #
                  </th>
                  {isAdmin && (
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      {t("Employee")}
                    </th>
                  )}
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Category")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Date")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Amount")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Status")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("Actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentPageData.map((expense) => (
                  <tr
                    key={expense.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">
                        {expense.expenseNumber}
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">
                        {expense.description}
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {expense.approver?.firstName}{" "}
                          {expense.approver?.lastName}
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getCategoryLabel(expense.category)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(expense.date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        <Price amount={Number(expense.amount)} />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 capitalize rounded-full text-xs font-medium ${getStatusColor(
                          expense.status,
                        )}`}
                      >
                        {expense.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedExpense(expense);
                            setDetailsModal(true);
                          }}
                          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                          title={t("View Details")}
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {/* Edit button - only for employees on their own pending/rejected expenses */}
                        {canEditExpense(expense) && (
                          <button
                            onClick={() => handleEditExpense(expense)}
                            className="p-2 text-blue-600 hover:text-blue-900 hover:bg-[#0d223f] rounded-lg transition-colors"
                            title={t("Edit")}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}

                        {/* Admin-only actions */}
                        {isAdmin && expense.status === "pending" && (
                          <>
                            <button
                              onClick={() => handleApproveExpense(expense.id)}
                              disabled={updating}
                              className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                              title={t("Approve")}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openRejectModal(expense.id)}
                              disabled={updating}
                              className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title={t("Reject")}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden divide-y divide-gray-200">
            {currentPageData.map((expense) => (
              <div
                key={expense.id}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="text-sm font-bold text-gray-900 mb-1">
                      {expense.expenseNumber}
                    </div>
                    {isAdmin && (
                      <div className="text-xs text-gray-600">
                        {expense.approver?.firstName}{" "}
                        {expense.approver?.lastName}
                      </div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      {formatDate(expense.date)}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedExpense(expense);
                      setDetailsModal(true);
                    }}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-2 text-xs text-gray-600">
                  {getCategoryLabel(expense.category)}
                </div>

                <div className="mb-3">
                  <p className="text-sm text-gray-700 line-clamp-2">
                    {expense.description}
                  </p>
                </div>

                <div className="flex items-center justify-between mb-3">
                  <div className="text-lg font-bold text-gray-900">
                    <Price amount={Number(expense.amount)} />
                  </div>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      expense.status,
                    )}`}
                  >
                    {expense.status}
                  </span>
                </div>

                {/* Edit button for employees */}
                {canEditExpense(expense) && (
                  <button
                    onClick={() => handleEditExpense(expense)}
                    className="w-full mb-2 inline-flex items-center justify-center gap-2 px-3 py-2 bg-[#0d223f] text-white rounded-lg hover:bg-[#0d223f] transition-colors text-sm"
                  >
                    <Edit className="w-4 h-4" />
                    {t("Edit Expense")}
                  </button>
                )}

                {/* Admin actions */}
                {isAdmin && expense.status === "pending" && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApproveExpense(expense.id)}
                      disabled={updating}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm"
                    >
                      <CheckCircle className="w-4 h-4" />
                      {t("Approve")}
                    </button>
                    <button
                      onClick={() => openRejectModal(expense.id)}
                      disabled={updating}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-sm"
                    >
                      <XCircle className="w-4 h-4" />
                      {t("Reject")}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12 px-4">
              <Receipt className="mx-auto w-12 h-12 sm:w-16 sm:h-16 text-gray-400" />
              <p className="mt-4 text-sm sm:text-base text-gray-600">
                {t("No expenses found")}
              </p>
            </div>
          )}

          {/* Pagination */}
          {finalTotalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={finalTotalPages}
              totalItems={finalTotalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                setCurrentPage(1);
              }}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              showPageSizeSelector={true}
              isLoading={loading}
              compact={true}
            />
          )}
        </div>
      </div>

      {/* Rejection Modal */}
      {rejectModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50"
          onClick={() => {
            setRejectModal(false);
            setExpenseToReject(null);
            setRejectionReason("");
          }}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-4 sm:px-6 py-4 rounded-t-xl sm:rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-base sm:text-xl font-bold text-white">
                    {t("Reject Expense")}
                  </h3>
                  <p className="text-xs sm:text-sm text-red-100">
                    {t("Please provide a reason for rejection")}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setRejectModal(false);
                  setExpenseToReject(null);
                  setRejectionReason("");
                }}
                className="text-white hover:text-red-100 p-1"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Rejection Reason")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  placeholder={t(
                    "Explain why this expense is being rejected...",
                  )}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  autoFocus
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t(
                    "This will be visible to the employee who submitted the expense",
                  )}
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    {t(
                      "This action will notify the employee and they may resubmit the expense after making corrections.",
                    )}
                  </span>
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setRejectModal(false);
                    setExpenseToReject(null);
                    setRejectionReason("");
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleRejectExpense}
                  disabled={updating || !rejectionReason.trim()}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {updating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t("Rejecting...")}
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      {t("Confirm Rejection")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Expense Modal */}
      {(createModal || editModal) && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50 overflow-y-auto"
          onClick={() => {
            setCreateModal(false);
            setEditModal(false);
            resetForm();
          }}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-2xl my-4 max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-black to-gray-700 px-4 sm:px-6 py-4 rounded-t-xl sm:rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
              <div>
                <h3 className="text-base sm:text-xl font-bold text-white">
                  {isEditMode ? t("Edit Expense") : t("Create New Expense")}
                </h3>
                <p className="text-xs sm:text-sm text-gray-300">
                  {isEditMode
                    ? t("Update expense details")
                    : isAdmin
                      ? t("Create expense for employee")
                      : t("Submit your expense for approval")}
                </p>
              </div>
              <button
                onClick={() => {
                  setCreateModal(false);
                  setEditModal(false);
                  resetForm();
                }}
                className="text-white hover:text-gray-300 p-1"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <form
              onSubmit={handleCreateExpense}
              className="p-4 sm:p-6 space-y-4"
            >
              {/* Employee Selection - Only for Admin in Create Mode */}
              {isAdmin && !isEditMode ? (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Employee")} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setShowEmployeeDropdown(!showEmployeeDropdown)
                      }
                      className="w-full flex items-center justify-between gap-2 px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-left focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <span
                        className={`truncate ${
                          selectedEmployeeId ? "text-gray-900" : "text-gray-500"
                        }`}
                      >
                        {selectedEmployeeId
                          ? selectedEmployeeName
                          : t("Select employee")}
                        {selectedEmployeeId === user?.id && (
                          <span className="ml-2 text-xs text-gray-500">
                            ({t("You")})
                          </span>
                        )}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 flex-shrink-0 transition-transform ${
                          showEmployeeDropdown ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {showEmployeeDropdown && (
                      <>
                        {/* Click-away layer */}
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setShowEmployeeDropdown(false)}
                        />
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-2xl z-20 overflow-hidden">
                          <div className="p-3 border-b border-gray-200 relative">
                            <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              autoFocus
                              value={employeeSearch}
                              onChange={(e) => setEmployeeSearch(e.target.value)}
                              placeholder={t("Search by name, phone or email...")}
                              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:border-black focus:outline-none"
                            />
                          </div>
                          <div className="overflow-y-auto max-h-64">
                            {employeesLoading ? (
                              <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {t("Loading...")}
                              </div>
                            ) : employees.length === 0 ? (
                              <div className="py-6 text-center text-sm text-gray-500">
                                {t("No users found")}
                              </div>
                            ) : (
                              employees.map((emp) => (
                                <button
                                  key={emp.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedEmployeeId(emp.id);
                                    setSelectedEmployeeName(
                                      getEmployeeLabel(emp),
                                    );
                                    setShowEmployeeDropdown(false);
                                  }}
                                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left ${
                                    emp.id === selectedEmployeeId
                                      ? "bg-gray-100"
                                      : ""
                                  }`}
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 truncate">
                                      {`${emp.firstName ?? ""} ${emp.lastName ?? ""}`.trim() ||
                                        emp.email ||
                                        "-"}
                                      {emp.id === user?.id && (
                                        <span className="ml-2 text-xs text-gray-500">
                                          ({t("You")})
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">
                                      {[
                                        emp.phone
                                          ? `${emp.phoneCode ?? ""}-${emp.phone}`
                                          : "",
                                        emp.email,
                                        typeof emp.role === "string"
                                          ? emp.role
                                          : emp.role?.name,
                                      ]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </div>
                                  </div>
                                  {emp.id === selectedEmployeeId && (
                                    <Check className="w-4 h-4 text-black flex-shrink-0" />
                                  )}
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : !isAdmin && !isEditMode ? (
                // Employee creating expense - show disabled field with their own name
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Employee")}
                  </label>
                  <input
                    type="text"
                    value={currentUserName || "You"}
                    disabled
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t("This expense will be submitted under your name")}
                  </p>
                </div>
              ) : null}

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Category")} <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                >
                  <option value="">{t("Select category")}</option>
                  {expenseCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {/* {cat.icon}  */}
                      {getCategoryName(cat)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Amount and Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Amount")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                    required
                    placeholder="0.00"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Date")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    max={new Date().toISOString().split("T")[0]}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Description")} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  required
                  rows={3}
                  placeholder={t("Describe your expense...")}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent resize-none"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Additional Notes")}
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  rows={2}
                  placeholder={t("Any additional information...")}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent resize-none"
                />
              </div>

              {/* Existing Attachments (Edit Mode) */}
              {isEditMode &&
                selectedExpense &&
                selectedExpense.attachments &&
                selectedExpense.attachments.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t("Current Attachments")}
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {selectedExpense.attachments.map((attachment, index) => (
                        <a
                          key={index}
                          href={attachment}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 border rounded-lg hover:bg-gray-50 transition-colors text-sm"
                        >
                          <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-700 truncate">
                            Receipt {index + 1}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

              {/* Attachments */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {isEditMode ? t("Add More Attachments") : t("Attachments")}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center justify-center"
                  >
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">
                      {t("Click to upload receipts")}
                    </span>
                    <span className="text-xs text-gray-500 mt-1">
                      {t("PDF or images, max 5MB each")}
                    </span>
                  </label>

                  {formData.attachments.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {formData.attachments.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded"
                        >
                          <span className="text-sm text-gray-700 truncate flex-1">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="ml-2 p-1 text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setCreateModal(false);
                    setEditModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  {t("Cancel")}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {isEditMode ? t("Updating...") : t("Submitting...")}
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      {isEditMode ? t("Update Expense") : t("Submit Expense")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Expense Details Modal */}
      {detailsModal && selectedExpense && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50 overflow-y-auto"
          onClick={() => {
            setDetailsModal(false);
            setSelectedExpense(null);
          }}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-3xl my-4 max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-black to-gray-700 px-4 sm:px-6 py-4 rounded-t-xl sm:rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
              <div>
                <h3 className="text-base sm:text-xl font-bold text-white">
                  {t("Expense Details")}
                </h3>
                <p className="text-xs sm:text-sm text-gray-300">
                  {selectedExpense.expenseNumber}
                </p>
              </div>
              <button
                onClick={() => {
                  setDetailsModal(false);
                  setSelectedExpense(null);
                }}
                className="text-white hover:text-gray-300 p-1"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4">
              {/* Edit/Delete Actions for Employee (Pending/Rejected only) */}
              {canEditExpense(selectedExpense) &&
                canDeleteExpense(selectedExpense) && (
                  <div className="bg-[#0d223f] rounded-lg p-4 flex gap-3">
                    <button
                      onClick={() => {
                        setDetailsModal(false);
                        handleEditExpense(selectedExpense);
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#0d223f] text-white rounded-lg hover:bg-[#0d223f] transition-colors font-medium"
                    >
                      <Edit className="w-5 h-5" />
                      {t("Edit Expense")}
                    </button>
                    <button
                      onClick={() => handleDeleteExpense(selectedExpense.id)}
                      disabled={updating}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 font-medium"
                    >
                      {updating ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                      {t("Delete")}
                    </button>
                  </div>
                )}

              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span
                  className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(
                    selectedExpense.status,
                  )}`}
                >
                  {selectedExpense.status.toUpperCase()}
                </span>
                <div className="text-2xl font-bold text-gray-900">
                  <Price amount={Number(selectedExpense.amount)} />
                </div>
              </div>

              {/* Admin Actions */}
              {isAdmin && selectedExpense.status === "pending" && (
                <div className="bg-gray-50 rounded-lg p-4 flex gap-3">
                  <button
                    onClick={() => handleApproveExpense(selectedExpense.id)}
                    disabled={updating}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 font-medium"
                  >
                    {updating ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <CheckCircle className="w-5 h-5" />
                    )}
                    {t("Approve")}
                  </button>
                  <button
                    onClick={() => openRejectModal(selectedExpense.id)}
                    disabled={updating}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 font-medium"
                  >
                    {updating ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <XCircle className="w-5 h-5" />
                    )}
                    {t("Reject")}
                  </button>
                </div>
              )}

              {/* Rejection Reason */}
              {selectedExpense.status === "rejected" &&
                selectedExpense.rejectionReason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      {t("Rejection Reason")}
                    </h4>
                    <p className="text-sm text-red-700">
                      {selectedExpense.rejectionReason}
                    </p>
                  </div>
                )}

              {/* Expense Information */}
              <div className="border rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-gray-900 text-lg">
                  {t("Expense Information")}
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">
                      {t("Category")}:
                    </span>
                    <p className="font-medium text-gray-900">
                      {getCategoryLabel(selectedExpense.category)}
                    </p>
                  </div>

                  <div>
                    <span className="text-sm text-gray-500">{t("Date")}:</span>
                    <p className="font-medium text-gray-900">
                      {formatDate(selectedExpense.date)}
                    </p>
                  </div>

                  {isAdmin && (
                    <>
                      <div>
                        <span className="text-sm text-gray-500">
                          {t("Employee")}:
                        </span>
                        <p className="font-medium text-gray-900">
                          {selectedExpense.approver?.firstName}{" "}
                          {selectedExpense.approver?.lastName}
                        </p>
                      </div>
                    </>
                  )}

                  <div>
                    <span className="text-sm text-gray-500">
                      {t("Submitted")}:
                    </span>
                    <p className="font-medium text-gray-900">
                      {formatDate(selectedExpense.createdAt)}
                    </p>
                  </div>

                  {selectedExpense.approvedAt && (
                    <div>
                      <span className="text-sm text-gray-500">
                        {t("Approved")}:
                      </span>
                      <p className="font-medium text-gray-900">
                        {formatDate(selectedExpense.approvedAt)}
                      </p>
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t">
                  <span className="text-sm text-gray-500">
                    {t("Description")}:
                  </span>
                  <p className="mt-1 text-gray-900">
                    {selectedExpense.description}
                  </p>
                </div>

                {selectedExpense.notes && (
                  <div className="pt-3 border-t">
                    <span className="text-sm text-gray-500">{t("Notes")}:</span>
                    <p className="mt-1 text-gray-700">
                      {selectedExpense.notes}
                    </p>
                  </div>
                )}
              </div>

              {/* Attachments */}
              {selectedExpense.attachments &&
                selectedExpense.attachments.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      {t("Attachments")}
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {selectedExpense.attachments.map((attachment, index) => (
                        <a
                          key={index}
                          href={attachment}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-gray-400" />
                            <span className="text-sm text-gray-700 truncate">
                              Receipt {index + 1}
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesManagementPage;
